import React, { useState, useEffect } from 'react'
import { Star, Copy } from 'lucide-react'

const CarePlanActions = ({ carePlan, carePlanId }) => {
  const [isFavorite, setIsFavorite] = useState(false)
  const [copySuccess, setCopySuccess] = useState('')

  // Check if this care plan is in favorites (from localStorage)
  useEffect(() => {
    const favorites = JSON.parse(localStorage.getItem('favorites') || '[]')
    setIsFavorite(favorites.includes(carePlanId))
  }, [carePlanId])

  const toggleFavorite = () => {
    const favorites = JSON.parse(localStorage.getItem('favorites') || '[]')
    if (isFavorite) {
      // Remove from favorites
      const updatedFavorites = favorites.filter(id => id !== carePlanId)
      localStorage.setItem('favorites', JSON.stringify(updatedFavorites))
      setIsFavorite(false)
    } else {
      // Add to favorites
      const updatedFavorites = [...favorites, carePlanId]
      localStorage.setItem('favorites', JSON.stringify(updatedFavorites))
      setIsFavorite(true)
    }

    // Dispatch custom event to notify other components
    window.dispatchEvent(new CustomEvent('favoritesChanged'))
  }

  // Fallback copy method for iframes where Clipboard API is blocked
  const fallbackCopyToClipboard = (text) => {
    const textArea = document.createElement('textarea')
    textArea.value = text
    
    // Avoid scrolling to bottom
    textArea.style.top = '0'
    textArea.style.left = '0'
    textArea.style.position = 'fixed'
    textArea.style.opacity = '0'
    
    document.body.appendChild(textArea)
    textArea.focus()
    textArea.select()
    
    let success = false
    try {
      success = document.execCommand('copy')
    } catch (err) {
      console.error('Fallback copy failed:', err)
    }
    
    document.body.removeChild(textArea)
    return success
  }

  const copyToClipboard = async () => {
    if (!carePlan.pgie) return

    const currentUrl = window.location.href

    const content = `
NURSING CARE PLAN: ${carePlan.title}
${carePlan.description}
Category: ${carePlan.category}

PROBLEMS:
${carePlan.pgie.problems.map((problem, index) => `${index + 1}. ${problem}`).join('\n')}

GOALS:
${carePlan.pgie.goals.map((goal, index) => `${index + 1}. ${goal}`).join('\n')}

INTERVENTIONS:
${carePlan.pgie.interventions.map((intervention, index) => `${index + 1}. ${intervention}`).join('\n')}

EVALUATION:
${carePlan.pgie.evaluation.map((evaluation, index) => `${index + 1}. ${evaluation}`).join('\n')}

From Qmed Nursing Care Plans
${currentUrl}
    `.trim()

    // Try modern Clipboard API first
    if (navigator.clipboard && window.isSecureContext) {
      try {
        await navigator.clipboard.writeText(content)
        setCopySuccess('Copied to clipboard!')
        setTimeout(() => setCopySuccess(''), 3000)
        return
      } catch (err) {
        // Clipboard API failed (likely iframe permission issue), try fallback
        console.log('Clipboard API failed, trying fallback...')
      }
    }
    
    // Fallback for iframes or older browsers
    const success = fallbackCopyToClipboard(content)
    if (success) {
      setCopySuccess('Copied to clipboard!')
    } else {
      setCopySuccess('Copy failed - please copy manually')
    }
    setTimeout(() => setCopySuccess(''), 3000)
  }

  return (
    <div className="action-section-compact">
      <h3>Quick Actions</h3>
      <div className="action-buttons-compact">
        {/* Favorite Button */}
        <button
          onClick={toggleFavorite}
          className={`action-button-compact favorite-button ${isFavorite ? 'active' : ''}`}
        >
          <Star size={18} fill={isFavorite ? '#ffc107' : 'none'} />
          {isFavorite ? 'Favorited' : 'Add to Favorites'}
        </button>

        {/* Copy Button */}
        {carePlan.pgie && (
          <button
            onClick={copyToClipboard}
            className="action-button-compact copy-button"
          >
            <Copy size={18} />
            Copy
          </button>
        )}
      </div>

      {/* Copy success message */}
      {copySuccess && (
        <div className="copy-success-message-compact">
          {copySuccess}
        </div>
      )}
    </div>
  )
}

export default CarePlanActions
